import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Camera, X, ChevronRight, MapPin, UserCheck, ShieldCheck, Upload } from "lucide-react";
import { api, fileUrl, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const CATEGORIES = [
    { value: "straight", label: "Straight", desc: "Men ↔ Women", color: "smash" },
    { value: "gay", label: "Gay / Lesbian", desc: "Same gender", color: "dash" },
    { value: "trans", label: "Trans", desc: "Trans-focused space", color: "white" },
];

const GENDERS = [
    { value: "male", label: "Man" },
    { value: "female", label: "Woman" },
    { value: "trans-man", label: "Trans Man" },
    { value: "trans-woman", label: "Trans Woman" },
    { value: "nonbinary", label: "Non-Binary" },
];

export default function Onboarding() {
    const nav = useNavigate();
    const { user, refresh } = useAuth();
    const [step, setStep] = useState(1);
    const [gender, setGender] = useState(user?.gender_identity || "");
    const [category, setCategory] = useState(user?.category || "");
    const [interestedIn, setInterestedIn] = useState(user?.interested_in || []);
    const [city, setCity] = useState(user?.location_city || "");
    const [bio, setBio] = useState(user?.bio || "");
    const [photos, setPhotos] = useState(user?.photos || []);
    const [verificationPhoto, setVerificationPhoto] = useState(user?.verification_photo || null);
    const [busy, setBusy] = useState(false);
    const fileInputRef = useRef(null);
    const verifyInputRef = useRef(null);

    const toggleInterest = (val) => {
        setInterestedIn((prev) =>
            prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val]
        );
    };

    const tryLocate = () => {
        if (!navigator.geolocation) return;
        navigator.geolocation.getCurrentPosition(
            async (pos) => {
                try {
                    await api.put("/profile/update", {
                        latitude: pos.coords.latitude,
                        longitude: pos.coords.longitude,
                    });
                    toast.success("Location saved");
                } catch {}
            },
            () => toast.error("Location denied — you can still use the app"),
            { enableHighAccuracy: false, timeout: 8000 }
        );
    };

    const uploadPhoto = async (file, kind) => {
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        fd.append("kind", kind);
        setBusy(true);
        try {
            const { data } = await api.post("/photos/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            if (kind === "verification") {
                setVerificationPhoto(data.url);
            } else {
                setPhotos((p) => [...p, data.url]);
            }
            toast.success("Uploaded");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
        } finally {
            setBusy(false);
        }
    };

    const removePhoto = async (url) => {
        try {
            await api.delete(`/photos?url=${encodeURIComponent(url)}`);
            setPhotos((p) => p.filter((u) => u !== url));
        } catch {}
    };

    const finishProfile = async () => {
        if (!gender || !category || interestedIn.length === 0 || !city) {
            toast.error("Please complete all required fields");
            return;
        }
        if (photos.length === 0) {
            toast.error("Add at least one photo");
            return;
        }
        setBusy(true);
        try {
            await api.put("/profile/update", {
                gender_identity: gender,
                category,
                interested_in: interestedIn,
                location_city: city,
                bio,
            });
            tryLocate();
            await refresh();
            toast.success("You're in.");
            nav("/discover");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Failed to save");
        } finally {
            setBusy(false);
        }
    };

    const progressPct = (step / 4) * 100;

    return (
        <div className="min-h-screen flex flex-col pb-8 text-white">
            {/* Progress */}
            <div className="px-6 pt-6">
                <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] uppercase tracking-[0.25em] text-white/50">Step {step} of 4</span>
                    <button
                        onClick={() => setStep(Math.max(1, step - 1))}
                        disabled={step === 1}
                        className="text-[10px] uppercase tracking-widest text-white/50 hover:text-white disabled:opacity-30"
                        data-testid="onboarding-back-btn"
                    >
                        Back
                    </button>
                </div>
                <div className="h-1 bg-white/10 overflow-hidden">
                    <motion.div
                        className="h-full bg-smash"
                        initial={false}
                        animate={{ width: `${progressPct}%` }}
                        transition={{ duration: 0.3 }}
                    />
                </div>
            </div>

            <div className="flex-1 px-6 pt-8">
                {step === 1 && (
                    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
                        <h2 className="font-display text-4xl text-white mb-1">WHO ARE</h2>
                        <h2 className="font-display text-4xl text-smash mb-5">YOU?</h2>
                        <p className="text-white/60 text-sm mb-6">Pick how you identify.</p>
                        <div className="space-y-2" data-testid="onboarding-gender-group">
                            {GENDERS.map((g) => (
                                <button
                                    key={g.value}
                                    onClick={() => setGender(g.value)}
                                    data-testid={`onboarding-gender-${g.value}`}
                                    className={`w-full h-14 px-4 border text-left font-display text-lg uppercase tracking-wider transition-colors ${
                                        gender === g.value
                                            ? "border-smash bg-smash/15 text-white"
                                            : "border-white/10 hover:border-white/30 text-white/80"
                                    }`}
                                >
                                    {g.label}
                                </button>
                            ))}
                        </div>
                    </motion.div>
                )}

                {step === 2 && (
                    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
                        <h2 className="font-display text-4xl text-white mb-1">PICK YOUR</h2>
                        <h2 className="font-display text-4xl text-dash mb-5">LANE.</h2>
                        <p className="text-white/60 text-sm mb-6">Categories are separate spaces. Pick one.</p>
                        <div className="space-y-3" data-testid="onboarding-category-group">
                            {CATEGORIES.map((c) => (
                                <button
                                    key={c.value}
                                    onClick={() => setCategory(c.value)}
                                    data-testid={`onboarding-category-${c.value}`}
                                    className={`w-full p-5 border-2 text-left transition-colors ${
                                        category === c.value
                                            ? "border-smash bg-smash/10"
                                            : "border-white/10 hover:border-white/30"
                                    }`}
                                >
                                    <div className="font-display text-2xl uppercase tracking-wider text-white">{c.label}</div>
                                    <div className="text-xs text-white/60 mt-1">{c.desc}</div>
                                </button>
                            ))}
                        </div>

                        <div className="mt-8">
                            <p className="text-[10px] uppercase tracking-[0.25em] text-white/50 mb-3">Interested In</p>
                            <div className="flex gap-2 flex-wrap">
                                {CATEGORIES.map((c) => (
                                    <button
                                        key={c.value}
                                        onClick={() => toggleInterest(c.value)}
                                        data-testid={`onboarding-interested-${c.value}`}
                                        className={`px-4 h-10 border text-xs uppercase tracking-wider transition-colors ${
                                            interestedIn.includes(c.value)
                                                ? "border-dash bg-dash/15 text-dash"
                                                : "border-white/10 text-white/60 hover:border-white/30"
                                        }`}
                                    >
                                        {c.label}
                                    </button>
                                ))}
                            </div>
                            <p className="text-[10px] text-white/40 mt-2">Pick one or more spaces to browse.</p>
                        </div>
                    </motion.div>
                )}

                {step === 3 && (
                    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
                        <h2 className="font-display text-4xl text-white mb-1">WHERE &</h2>
                        <h2 className="font-display text-4xl text-smash mb-5">WHY.</h2>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.25em] text-white/60 mb-1 flex items-center gap-1">
                                    <MapPin className="w-3 h-3" /> City
                                </label>
                                <input
                                    type="text"
                                    placeholder="Austin, TX"
                                    value={city}
                                    onChange={(e) => setCity(e.target.value)}
                                    data-testid="onboarding-city-input"
                                    className="w-full h-12 bg-ink-800 border border-white/10 focus:border-smash px-3 text-white outline-none"
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.25em] text-white/60 mb-1">Short Bio</label>
                                <textarea
                                    placeholder="Tell them what you're about (optional)"
                                    value={bio}
                                    onChange={(e) => setBio(e.target.value)}
                                    rows={5}
                                    maxLength={280}
                                    data-testid="onboarding-bio-input"
                                    className="w-full bg-ink-800 border border-white/10 focus:border-smash p-3 text-white outline-none resize-none"
                                />
                                <div className="text-right text-[10px] text-white/40 mt-1">{bio.length}/280</div>
                            </div>
                        </div>
                    </motion.div>
                )}

                {step === 4 && (
                    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
                        <h2 className="font-display text-4xl text-white mb-1">SHOW</h2>
                        <h2 className="font-display text-4xl text-smash mb-5">YOURSELF.</h2>
                        <p className="text-white/60 text-sm mb-4">Add up to 6 photos. First photo is your headliner.</p>

                        <div className="grid grid-cols-3 gap-2" data-testid="onboarding-photos-grid">
                            {photos.map((p, i) => (
                                <div key={p} className="aspect-square relative group border border-white/10" data-testid={`onboarding-photo-${i}`}>
                                    <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
                                    <button
                                        onClick={() => removePhoto(p)}
                                        data-testid={`onboarding-remove-photo-${i}`}
                                        className="absolute top-1 right-1 w-6 h-6 bg-black/70 text-white flex items-center justify-center hover:bg-smash"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                </div>
                            ))}
                            {photos.length < 6 && (
                                <button
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={busy}
                                    data-testid="onboarding-add-photo-btn"
                                    className="aspect-square border border-dashed border-white/20 hover:border-smash flex flex-col items-center justify-center gap-1 text-white/50 hover:text-smash transition-colors"
                                >
                                    <Camera className="w-5 h-5" />
                                    <span className="text-[10px] uppercase tracking-widest">Add</span>
                                </button>
                            )}
                        </div>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            className="hidden"
                            onChange={(e) => uploadPhoto(e.target.files?.[0], "profile")}
                            data-testid="onboarding-photo-file-input"
                        />

                        <div className="mt-7 border border-dash/40 bg-dash/5 p-4">
                            <div className="flex items-center gap-2 mb-2">
                                <ShieldCheck className="w-4 h-4 text-dash" />
                                <span className="text-[10px] uppercase tracking-[0.25em] text-dash">Verification (optional)</span>
                            </div>
                            <p className="text-[11px] text-white/70 leading-relaxed">
                                Take a selfie showing your face clearly to earn a <span className="text-dash">VERIFIED</span> badge and boost your matches.
                            </p>
                            {verificationPhoto ? (
                                <div className="mt-3 flex items-center gap-3">
                                    <img src={fileUrl(verificationPhoto)} alt="verification" className="w-16 h-16 object-cover border border-dash/40" />
                                    <div className="text-[11px] text-dash uppercase tracking-widest">Under review</div>
                                </div>
                            ) : (
                                <>
                                    <button
                                        onClick={() => verifyInputRef.current?.click()}
                                        data-testid="onboarding-upload-verify-btn"
                                        className="mt-3 h-10 px-4 bg-dash text-black font-display text-xs uppercase tracking-widest hover:bg-dash/90"
                                    >
                                        <Upload className="inline w-3 h-3 mr-1" /> Upload Verification Selfie
                                    </button>
                                    <input
                                        ref={verifyInputRef}
                                        type="file"
                                        accept="image/jpeg,image/png,image/webp"
                                        className="hidden"
                                        onChange={(e) => uploadPhoto(e.target.files?.[0], "verification")}
                                        data-testid="onboarding-verify-file-input"
                                    />
                                </>
                            )}
                        </div>
                    </motion.div>
                )}
            </div>

            <div className="px-6 pt-6">
                {step < 4 ? (
                    <button
                        onClick={() => {
                            if (step === 1 && !gender) { toast.error("Pick your identity"); return; }
                            if (step === 2 && (!category || interestedIn.length === 0)) { toast.error("Pick your lane and interests"); return; }
                            if (step === 3 && !city) { toast.error("City required"); return; }
                            setStep(step + 1);
                        }}
                        data-testid="onboarding-next-btn"
                        className="w-full h-14 bg-smash hover:bg-smash/90 text-white font-display text-lg tracking-wider uppercase flex items-center justify-center gap-2"
                    >
                        Continue <ChevronRight className="w-5 h-5" />
                    </button>
                ) : (
                    <button
                        onClick={finishProfile}
                        disabled={busy || photos.length === 0}
                        data-testid="onboarding-finish-btn"
                        className="w-full h-14 bg-smash hover:bg-smash/90 disabled:opacity-50 text-white font-display text-lg tracking-wider uppercase flex items-center justify-center gap-2"
                    >
                        <UserCheck className="w-5 h-5" /> {busy ? "Saving…" : "Launch Profile"}
                    </button>
                )}
            </div>
        </div>
    );
}
